import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// REQUEST interceptor — добавляет токен
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Accept-Language'] = localStorage.getItem('language') || 'uz';
    return config;
  },
  (error) => Promise.reject(error)
);

// RESPONSE interceptor — при 401 автоматически logout и redirect на login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Только если мы не уже на странице логина
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) =>
  api.post('/auth/login/', { username, password });

export const logout = () =>
  api.post('/auth/logout/');

export const getCurrentUser = () =>
  api.get('/auth/user/');

// Reports
export const getReports = (params = {}) =>
  api.get('/reports/reports/', { params });

export const getReport = (id) =>
  api.get(`/reports/reports/${id}/`);

export const createReport = (data) =>
  api.post('/reports/reports/', data);

export const updateReport = (id, data) =>
  api.patch(`/reports/reports/${id}/`, data);

export const deleteReport = (id) =>
  api.delete(`/reports/reports/${id}/`);

export const approveReport = (id) =>
  api.post(`/reports/reports/${id}/approve/`);

export const rejectReport = (id, reason) =>
  api.post(`/reports/reports/${id}/reject/`, { rejection_reason: reason });

export const bulkApproveReports = (reportIds) =>
  api.post('/reports/reports/bulk_approve/', { report_ids: reportIds });

export const bulkRejectReports = (reportIds, rejectionReason) =>
  api.post('/reports/reports/bulk_reject/', { report_ids: reportIds, rejection_reason: rejectionReason });

export const submitReport = (id) =>
  api.post(`/reports/reports/${id}/submit/`);

export const getSubmissionStats = (params = {}) =>
  api.get('/reports/reports/submission_stats/', { params });

export const getMyReportTemplates = () =>
  api.get('/reports/templates/my_templates/');

export const getLastReport = (params = {}) =>
  api.get('/reports/reports/last_report/', { params });

export const getTemplateByDepartment = (departmentId) =>
  api.get('/reports/templates/by_department/', { params: { department_id: departmentId } });

// Report Comments — комментарии к полям отчёта
export const getReportComments = (params = {}) =>
  api.get('/reports/comments/', { params });

export const getFieldComments = (reportId, fieldKey) =>
  api.get('/reports/comments/by_field/', { params: { report: reportId, field_key: fieldKey } });

export const getFieldCommentSummary = (reportId) =>
  api.get('/reports/comments/field_summary/', { params: { report: reportId } });

export const createReportComment = (data) =>
  api.post('/reports/comments/', data);

export const deleteReportComment = (id) =>
  api.delete(`/reports/comments/${id}/`);

export const resolveReportComment = (id) =>
  api.post(`/reports/comments/${id}/resolve/`);

export const unresolveReportComment = (id) =>
  api.post(`/reports/comments/${id}/unresolve/`);

// Periods
export const getPeriods = () =>
  api.get('/reports/periods/');

export const getPeriod = (id) =>
  api.get(`/reports/periods/${id}/`);

// Analytics
export const getAnalytics = () =>
  api.get('/reports/analytics/overview/');

export const getReportTrends = (params = {}) =>
  api.get('/reports/analytics/trends/', { params });

// Documents
export const getDocuments = (params = {}) =>
  api.get('/documents/documents/', { params });

export const getDocument = (id) =>
  api.get(`/documents/documents/${id}/`);

export const createDocument = (data) =>
  api.post('/documents/documents/', data);

export const updateDocument = (id, data) =>
  api.patch(`/documents/documents/${id}/`, data);

export const deleteDocument = (id) =>
  api.delete(`/documents/documents/${id}/`);

export const changeDocumentStatus = (id, data) =>
  api.post(`/documents/documents/${id}/change_status/`, data);

export const addDocumentItem = (id, data) =>
  api.post(`/documents/documents/${id}/add_item/`, data);

export const getMyDocuments = (params = {}) =>
  api.get('/documents/documents/my_documents/', { params });

export const getOverdueDocuments = () =>
  api.get('/documents/documents/overdue/');

export const getUpcomingDeadlines = () =>
  api.get('/documents/documents/upcoming_deadlines/');

export const getDocumentStatistics = () =>
  api.get('/documents/documents/statistics/');

// Document Items
export const getDocumentItems = (params = {}) =>
  api.get('/documents/items/', { params });

export const getDocumentItem = (id) =>
  api.get(`/documents/items/${id}/`);

export const changeItemStatus = (id, data) =>
  api.post(`/documents/items/${id}/change_status/`, data);

export const addItemExecutor = (id, data) =>
  api.post(`/documents/items/${id}/add_executor/`, data);

// Executors
export const getMyPendingExecutions = () =>
  api.get('/documents/executors/my_pending/');
export const completeExecution = (id, data = {}) =>
  api.post(`/documents/executors/${id}/complete/`, data);

// Загрузка файла к документу (FormData: document, file, description)
export const uploadDocumentFile = (formData) =>
  api.post('/documents/files/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

// Справочники документооборота
export const getDocumentTypes = () =>
  api.get('/documents/types/');

export const getDocumentStatuses = () =>
  api.get('/documents/statuses/');

export const getAuthorities = () =>
  api.get('/documents/authorities/');

export const getPriorities = () =>
  api.get('/documents/priorities/');

export const getDepartments = () =>
  api.get('/documents/departments/');

// Planning — Планы
export const getPlans = (params = {}) =>
  api.get('/planning/plans/', { params });

export const getPlan = (id) =>
  api.get(`/planning/plans/${id}/`);

export const createPlan = (data) =>
  api.post('/planning/plans/', data);

export const updatePlan = (id, data) =>
  api.patch(`/planning/plans/${id}/`, data);

export const deletePlan = (id) =>
  api.delete(`/planning/plans/${id}/`);

export const activatePlan = (id) =>
  api.post(`/planning/plans/${id}/activate/`);

export const completePlan = (id) =>
  api.post(`/planning/plans/${id}/complete/`);

export const changePlanStatus = (id, data) =>
  api.post(`/planning/plans/${id}/change_status/`, data);

export const getMyPlans = (params = {}) =>
  api.get('/planning/plans/my_plans/', { params });

export const getPlanStatistics = (params = {}) =>
  api.get('/planning/plans/statistics/', { params });

export const getPlanFact = (id) =>
  api.get(`/planning/plans/${id}/plan_fact/`);

// Planning — Задачи
export const getPlanTasks = (params = {}) =>
  api.get('/planning/tasks/', { params });

export const getPlanTask = (id) =>
  api.get(`/planning/tasks/${id}/`);

export const reportTaskProgress = (id, data) =>
  api.post(`/planning/tasks/${id}/report_progress/`, data);

export const changeTaskStatus = (id, data) =>
  api.post(`/planning/tasks/${id}/change_status/`, data);

// Analytics — Централизованная аналитика
export const getDashboardAnalytics = (params = {}) =>
  api.get('/analytics/dashboard/', { params });

export const getDepartmentKPI = (params = {}) =>
  api.get('/analytics/department_kpi/', { params });

export const getTrends = (params = {}) =>
  api.get('/analytics/trends/', { params });

export const getUserActivity = (params = {}) =>
  api.get('/analytics/user_activity/', { params });

export const getExecutionDiscipline = (params = {}) =>
  api.get('/analytics/execution_discipline/', { params });

export const comparePeriods = (params = {}) =>
  api.get('/analytics/compare_periods/', { params });

export const getDirectorOverview = () =>
  api.get('/analytics/director_overview/');

// Глобальный поиск
export const globalSearch = (q, params = {}) =>
  api.get('/analytics/search/', { params: { q, ...params } });

// Сравнение год к году
export const getYearComparison = (params = {}) =>
  api.get('/analytics/year_comparison/', { params });

// Карта рисков
export const getRiskMap = (params = {}) =>
  api.get('/analytics/risk_map/', { params });

// Рейтинг отделов с историей
export const getRatingHistory = (params = {}) =>
  api.get('/analytics/rating_history/', { params });

export const getRatingTable = (params = {}) =>
  api.get('/analytics/rating_table/', { params });

export const generateRatingSnapshots = (data) =>
  api.post('/analytics/generate_snapshots/', data);

// KPI сотрудников
export const getEmployeeKPI = (params = {}) =>
  api.get('/analytics/employee_kpi/', { params });

// Accounts — Пользователи
export const getUsers = (params = {}) =>
  api.get('/accounts/users/', { params });

export const getUser = (id) =>
  api.get(`/accounts/users/${id}/`);

export const createUser = (data) =>
  api.post('/accounts/users/', data);

export const updateUser = (id, data) =>
  api.patch(`/accounts/users/${id}/`, data);

export const deleteUser = (id) =>
  api.delete(`/accounts/users/${id}/`);

// Аудит-лог
export const getAuditLogs = (params = {}) =>
  api.get('/accounts/audit-logs/', { params });

// Профиль
export const getProfile = () =>
  api.get('/auth/profile/');

export const updateProfile = (data) =>
  api.patch('/auth/profile/', data);

export const changePassword = (data) =>
  api.post('/auth/change-password/', data);

// AI Assistant — ИИ-помощник
export const aiAsk = (data) =>
  api.post('/ai/ask/', data);

export const aiGetSummary = (params = {}) =>
  api.get('/ai/summary/', { params });

export const aiGenerateSummary = (data) =>
  api.post('/ai/summary/', data);

export const aiGetTrends = (params = {}) =>
  api.get('/ai/trends/', { params });

export const aiGenerateTrends = (data) =>
  api.post('/ai/trends/', data);

export const aiGetAnomalies = (params = {}) =>
  api.get('/ai/anomalies/', { params });

export const aiGenerateAnomalies = (data) =>
  api.post('/ai/anomalies/', data);

export const aiGetForecast = (params = {}) =>
  api.get('/ai/forecast/', { params });

export const aiGenerateForecast = (data) =>
  api.post('/ai/forecast/', data);

export const aiCompare = (params = {}) =>
  api.get('/ai/compare/', { params });

export const aiKeyMoments = (params = {}) =>
  api.get('/ai/key_moments/', { params });

export const aiSuggestedQuestions = () =>
  api.get('/ai/suggested_questions/');

export const aiQuickInsights = (params = {}) =>
  api.get('/ai/quick_insights/', { params });

export const aiGetRecommendations = (params = {}) =>
  api.get('/ai/recommendations/', { params });

export const aiUpdateRecommendation = (id, data) =>
  api.patch(`/ai/recommendations/${id}/`, data);

export const aiGenerateRecommendations = () =>
  api.post('/ai/recommendations/generate/');

export const aiActiveRecommendations = () =>
  api.get('/ai/recommendations/active/');

export const aiGetConversations = (params = {}) =>
  api.get('/ai/conversations/', { params });

export const aiSendFeedback = (id, data) =>
  api.post(`/ai/conversations/${id}/feedback/`, data);

// Exports — Экспорт в Excel/PDF/Word
export const exportKpiExcel = (params = {}) =>
  api.get('/exports/kpi_excel/', { params, responseType: 'blob' });

export const exportKpiPdf = (params = {}) =>
  api.get('/exports/kpi_pdf/', { params, responseType: 'blob' });

export const exportDisciplineExcel = (params = {}) =>
  api.get('/exports/discipline_excel/', { params, responseType: 'blob' });

export const exportDisciplinePdf = (params = {}) =>
  api.get('/exports/discipline_pdf/', { params, responseType: 'blob' });

export const exportReportsExcel = (params = {}) =>
  api.get('/exports/reports_excel/', { params, responseType: 'blob' });

export const exportDocumentsExcel = (params = {}) =>
  api.get('/exports/documents_excel/', { params, responseType: 'blob' });

export const exportPlansExcel = (params = {}) =>
  api.get('/exports/plans_excel/', { params, responseType: 'blob' });

export const exportPlanFactExcel = (params = {}) =>
  api.get('/exports/plan_fact_excel/', { params, responseType: 'blob' });

export const exportReportWord = (params = {}) =>
  api.get('/exports/report_word/', { params, responseType: 'blob' });

export const exportAnalyticalWord = (params = {}) =>
  api.get('/exports/analytical_word/', { params, responseType: 'blob' });

export const exportAnalyticalPdf = (params = {}) =>
  api.get('/exports/analytical_pdf/', { params, responseType: 'blob' });

export const exportEmpKpiExcel = (params = {}) =>
  api.get('/exports/emp_kpi_excel/', { params, responseType: 'blob' });

export const exportEmpKpiWord = (params = {}) =>
  api.get('/exports/emp_kpi_word/', { params, responseType: 'blob' });

// Presentations — Презентации
export const getPresentationTemplates = (params = {}) =>
  api.get('/presentations/templates/', { params });

export const getPresentationTemplate = (id) =>
  api.get(`/presentations/templates/${id}/`);

export const createPresentationTemplate = (data) =>
  api.post('/presentations/templates/', data);

export const updatePresentationTemplate = (id, data) =>
  api.put(`/presentations/templates/${id}/`, data);

export const deletePresentationTemplate = (id) =>
  api.delete(`/presentations/templates/${id}/`);

export const createDefaultTemplates = () =>
  api.post('/presentations/templates/defaults/');

export const getPresentations = (params = {}) =>
  api.get('/presentations/presentations/', { params });

export const getPresentation = (id) =>
  api.get(`/presentations/presentations/${id}/`);

export const createPresentation = (data) =>
  api.post('/presentations/presentations/', data);

export const updatePresentation = (id, data) =>
  api.put(`/presentations/presentations/${id}/`, data);

export const deletePresentation = (id) =>
  api.delete(`/presentations/presentations/${id}/`);

export const generatePresentation = (id) =>
  api.post(`/presentations/presentations/${id}/generate/`);

export const downloadPresentation = (id) =>
  api.get(`/presentations/presentations/${id}/download/`, { responseType: 'blob' });

export const previewPresentation = (id) =>
  api.get(`/presentations/presentations/${id}/preview/`);

export const regeneratePresentation = (id) =>
  api.post(`/presentations/presentations/${id}/regenerate/`);

export const getPresentationStatistics = () =>
  api.get('/presentations/presentations/statistics/');

// Справочники — полный CRUD (admin)
export const getDirDepartments = (params = {}) =>
  api.get('/directories/departments/', { params });
export const createDirDepartment = (data) =>
  api.post('/directories/departments/', data);
export const updateDirDepartment = (id, data) =>
  api.patch(`/directories/departments/${id}/`, data);
export const deleteDirDepartment = (id) =>
  api.delete(`/directories/departments/${id}/`);
export const restoreDirDepartment = (id) =>
  api.post(`/directories/departments/${id}/restore/`);

export const getDirAuthorities = (params = {}) =>
  api.get('/directories/authorities/', { params });
export const createDirAuthority = (data) =>
  api.post('/directories/authorities/', data);
export const updateDirAuthority = (id, data) =>
  api.patch(`/directories/authorities/${id}/`, data);
export const deleteDirAuthority = (id) =>
  api.delete(`/directories/authorities/${id}/`);
export const restoreDirAuthority = (id) =>
  api.post(`/directories/authorities/${id}/restore/`);

export const getDirDocumentTypes = (params = {}) =>
  api.get('/directories/document-types/', { params });
export const createDirDocumentType = (data) =>
  api.post('/directories/document-types/', data);
export const updateDirDocumentType = (id, data) =>
  api.patch(`/directories/document-types/${id}/`, data);
export const deleteDirDocumentType = (id) =>
  api.delete(`/directories/document-types/${id}/`);
export const restoreDirDocumentType = (id) =>
  api.post(`/directories/document-types/${id}/restore/`);

export const getDirDocumentStatuses = (params = {}) =>
  api.get('/directories/document-statuses/', { params });
export const createDirDocumentStatus = (data) =>
  api.post('/directories/document-statuses/', data);
export const updateDirDocumentStatus = (id, data) =>
  api.patch(`/directories/document-statuses/${id}/`, data);
export const deleteDirDocumentStatus = (id) =>
  api.delete(`/directories/document-statuses/${id}/`);
export const restoreDirDocumentStatus = (id) =>
  api.post(`/directories/document-statuses/${id}/restore/`);

export const getDirPriorities = (params = {}) =>
  api.get('/directories/priorities/', { params });
export const createDirPriority = (data) =>
  api.post('/directories/priorities/', data);
export const updateDirPriority = (id, data) =>
  api.patch(`/directories/priorities/${id}/`, data);
export const deleteDirPriority = (id) =>
  api.delete(`/directories/priorities/${id}/`);
export const restoreDirPriority = (id) =>
  api.post(`/directories/priorities/${id}/restore/`);

export const getDirTaskCategories = (params = {}) =>
  api.get('/directories/task-categories/', { params });
export const createDirTaskCategory = (data) =>
  api.post('/directories/task-categories/', data);
export const updateDirTaskCategory = (id, data) =>
  api.patch(`/directories/task-categories/${id}/`, data);
export const deleteDirTaskCategory = (id) =>
  api.delete(`/directories/task-categories/${id}/`);
export const restoreDirTaskCategory = (id) =>
  api.post(`/directories/task-categories/${id}/restore/`);

// Системные настройки
export const getSystemSettings = (params = {}) =>
  api.get('/settings/', { params });

// Бэкапы
export const getBackups = () =>
  api.get('/backups/');

export const createBackup = () =>
  api.post('/backups/create/');

export const downloadBackup = (filename) =>
  api.get(`/backups/${filename}/download/`, { responseType: 'blob' });

export const deleteBackup = (filename) =>
  api.delete(`/backups/${filename}/delete/`);

export const getSettingsByCategory = () =>
  api.get('/settings/by_category/');

export const updateSystemSetting = (id, data) =>
  api.patch(`/settings/${id}/`, data);

export const bulkUpdateSettings = (data) =>
  api.post('/settings/bulk_update/', data);

export const createSystemSetting = (data) =>
  api.post('/settings/', data);

export const deleteSystemSetting = (id) =>
  api.delete(`/settings/${id}/`);

// Weekly Control — Еженедельный контроль (Приоритет 13)
export const getWeeklySummary = (params = {}) =>
  api.get('/planning/plans/weekly_summary/', { params });

export const getExecutionCheck = (planId) =>
  api.get(`/planning/plans/${planId}/execution_check/`);

export const carryOverTasks = (planId, data) =>
  api.post(`/planning/plans/${planId}/carry_over/`, data);

export const getCarryOverStats = (params = {}) =>
  api.get('/planning/plans/carry_over_stats/', { params });

export const getCenterPlan = (params = {}) =>
  api.get('/planning/plans/center_plan/', { params });

export const getRelatedDepartments = () =>
  api.get('/planning/plans/related_departments/');

// Report Constructor — Конструктор информаций (Приоритет 14)
export const getConstructorTemplates = (params = {}) =>
  api.get('/constructor/templates/', { params });

export const getConstructorTemplate = (id) =>
  api.get(`/constructor/templates/${id}/`);

export const createConstructorTemplate = (data) =>
  api.post('/constructor/templates/', data);

export const updateConstructorTemplate = (id, data) =>
  api.patch(`/constructor/templates/${id}/`, data);

export const deleteConstructorTemplate = (id) =>
  api.delete(`/constructor/templates/${id}/`);

export const getGeneratedReports = (params = {}) =>
  api.get('/constructor/reports/', { params });

export const generateConstructorReport = (data) =>
  api.post('/constructor/reports/generate/', data);

export const downloadGeneratedReport = (id) =>
  api.get(`/constructor/reports/${id}/download/`, { responseType: 'blob' });

export const deleteGeneratedReport = (id) =>
  api.delete(`/constructor/reports/${id}/`);

// ─── Расписания автоматической генерации ─────────────────────

export const getScheduledReports = (params = {}) =>
  api.get('/constructor/schedules/', { params });

export const getScheduledReport = (id) =>
  api.get(`/constructor/schedules/${id}/`);

export const createScheduledReport = (data) =>
  api.post('/constructor/schedules/', data);

export const updateScheduledReport = (id, data) =>
  api.patch(`/constructor/schedules/${id}/`, data);

export const deleteScheduledReport = (id) =>
  api.delete(`/constructor/schedules/${id}/`);

export const toggleScheduledReport = (id) =>
  api.post(`/constructor/schedules/${id}/toggle_active/`);

export const runScheduledReportNow = (id) =>
  api.post(`/constructor/schedules/${id}/run_now/`);

// ─── Консолидированные (сводные) отчёты ─────────────────────

export const getConsolidatedReports = (params = {}) =>
  api.get('/constructor/consolidated/', { params });

export const getConsolidatedReport = (id) =>
  api.get(`/constructor/consolidated/${id}/`);

export const createConsolidatedReport = (data) =>
  api.post('/constructor/consolidated/', data);

export const updateConsolidatedReport = (id, data) =>
  api.patch(`/constructor/consolidated/${id}/`, data);

export const deleteConsolidatedReport = (id) =>
  api.delete(`/constructor/consolidated/${id}/`);

export const refreshConsolidatedData = (id) =>
  api.post(`/constructor/consolidated/${id}/refresh/`);

export const updateConsolidatedNarratives = (id, data) =>
  api.patch(`/constructor/consolidated/${id}/narratives/`, data);

export const generateConsolidatedNarratives = (id, data = {}) =>
  api.post(`/constructor/consolidated/${id}/generate_narratives/`, data);

export const generateConsolidatedDoc = (id, data) =>
  api.post(`/constructor/consolidated/${id}/generate/`, data);

export const downloadConsolidatedDoc = (id, format = 'word') =>
  api.get(`/constructor/consolidated/${id}/download/?file_format=${format}`, { responseType: 'blob' });

export const approveConsolidatedReport = (id) =>
  api.post(`/constructor/consolidated/${id}/approve/`);

// ─── HR — Отдел кадров ──────────────────────────────────────

// Сотрудники
export const getEmployees = (params = {}) =>
  api.get('/hr/employees/', { params });

export const getEmployee = (id) =>
  api.get(`/hr/employees/${id}/`);

export const getEmployeesByDepartment = () =>
  api.get('/hr/employees/by_department/');

// Посещаемость
export const getAttendance = (params = {}) =>
  api.get('/hr/attendance/', { params });

export const getAttendanceToday = (params = {}) =>
  api.get('/hr/attendance/today/', { params });

export const getAttendanceSummary = (params = {}) =>
  api.get('/hr/attendance/summary/', { params });

export const bulkMarkAttendance = (data) =>
  api.post('/hr/attendance/bulk_mark/', data);

// Подтверждение посещаемости (HR)
export const getAttendanceConfirmationStatus = (params = {}) =>
  api.get('/hr/attendance/confirmation_status/', { params });
export const confirmAttendance = (data) =>
  api.post('/hr/attendance/confirm/', data);
export const unconfirmAttendance = (params = {}) =>
  api.delete('/hr/attendance/unconfirm/', { params });

// Переводы
export const getTransfers = (params = {}) =>
  api.get('/hr/transfers/', { params });

export const createTransfer = (data) =>
  api.post('/hr/transfers/', data);

// Импорт сотрудников из Excel
export const uploadEmployeesPreview = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/hr/employees/upload_preview/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const uploadEmployeesConfirm = (file, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (options.default_password) formData.append('default_password', options.default_password);
  if (options.default_role) formData.append('default_role', options.default_role);
  if (options.deactivate_test) formData.append('deactivate_test', 'true');
  if (options.skip_duplicates !== undefined)
    formData.append('skip_duplicates', options.skip_duplicates ? 'true' : 'false');
  return api.post('/hr/employees/upload_confirm/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const createEmployee = (data) => api.post('/hr/employees/', data);
export const updateEmployee = (id, data) => api.patch(`/hr/employees/${id}/`, data);
export const deleteEmployee = (id) => api.delete(`/hr/employees/${id}/`);

// ─── Объяснительные по опозданиям ─────────────────────────────
export const getExplanations = (params = {}) =>
  api.get('/hr/explanations/', { params });

export const requestExplanation = (data) =>
  api.post('/hr/explanations/request/', data);

export const submitExplanation = (id, file) => {
  const formData = new FormData();
  formData.append('pdf_file', file);
  return api.post(`/hr/explanations/${id}/submit/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const reviewExplanation = (id, hrNote = '') =>
  api.patch(`/hr/explanations/${id}/review/`, { hr_note: hrNote });

export const downloadExplanation = (id) =>
  api.get(`/hr/explanations/${id}/download/`, { responseType: 'blob' });

// ─── KPI сотрудников ─────────────────────────────────────────
export const getKPITemplates = (params = {}) =>
  api.get('/kpi/templates/', { params });

export const getKPITemplate = (id) =>
  api.get(`/kpi/templates/${id}/`);

export const getMyKPITemplate = () =>
  api.get('/kpi/templates/my_template/');

export const createKPITemplate = (data) =>
  api.post('/kpi/templates/', data);

export const updateKPITemplate = (id, data) =>
  api.patch(`/kpi/templates/${id}/`, data);

export const getKPIRecords = (params = {}) =>
  api.get('/kpi/records/', { params });

export const getKPIRecord = (id) =>
  api.get(`/kpi/records/${id}/`);

export const createKPIRecord = (data) =>
  api.post('/kpi/records/', data);

export const updateKPIRecord = (id, data) =>
  api.patch(`/kpi/records/${id}/`, data);

export const getMyTeamKPI = (params = {}) =>
  api.get('/kpi/records/my_team/', { params });

export const submitKPIRecord = (id) =>
  api.post(`/kpi/records/${id}/submit/`);

export const approveKPIRecord = (id) =>
  api.post(`/kpi/records/${id}/approve/`);

export const rejectKPIRecord = (id) =>
  api.post(`/kpi/records/${id}/reject/`);

export const autoCalculateKPI = (id) =>
  api.post(`/kpi/records/${id}/auto_calculate/`);

export const bulkAutoCalculateKPI = (data) =>
  api.post('/kpi/records/bulk_auto_calculate/', data);

export const downloadKPIWord = (id) =>
  api.get(`/kpi/records/${id}/download_word/`, { responseType: 'blob' });

export const downloadKPISummaryExcel = (params = {}) =>
  api.get('/kpi/records/summary_excel/', { params, responseType: 'blob' });

export const getDailyKPIEntries = (params = {}) =>
  api.get('/kpi/daily/', { params });

export const bulkDailyKPIEntry = (data) =>
  api.post('/kpi/daily/bulk_entry/', data);

export const getMonthlyKPITracker = (params = {}) =>
  api.get('/kpi/daily/monthly_tracker/', { params });

// ─── KPI-планы отделов ───────────────────────────────────────
export const getDeptKPIPlans = (params = {}) =>
  api.get('/kpi/dept-plans/', { params });

export const getDeptKPIPlan = (id) =>
  api.get(`/kpi/dept-plans/${id}/`);

export const createDeptKPIPlan = (data) =>
  api.post('/kpi/dept-plans/', data);

export const updateDeptKPIPlan = (id, data) =>
  api.patch(`/kpi/dept-plans/${id}/`, data);

export const deleteDeptKPIPlan = (id) =>
  api.delete(`/kpi/dept-plans/${id}/`);

export const submitDeptKPIPlan = (id) =>
  api.post(`/kpi/dept-plans/${id}/submit/`);

export const approveDeptKPIPlan = (id) =>
  api.post(`/kpi/dept-plans/${id}/approve/`);

export const rejectDeptKPIPlan = (id) =>
  api.post(`/kpi/dept-plans/${id}/reject/`);

export const initDeptKPIPlanFromTemplate = (id) =>
  api.post(`/kpi/dept-plans/${id}/init_from_template/`);

export const autoAggregateDeptKPIPlan = (id) =>
  api.post(`/kpi/dept-plans/${id}/auto_aggregate/`);

export const downloadDeptKPIPlanWord = (id) =>
  api.get(`/kpi/dept-plans/${id}/download_word/`, { responseType: 'blob' });

// ─── Уведомления ─────────────────────────────────────────────
export const getNotifications = (params) =>
  api.get('/notifications/', { params });

export const getUnreadCount = () =>
  api.get('/notifications/unread_count/');

export const markNotificationRead = (id) =>
  api.patch(`/notifications/${id}/read/`);

export const markAllNotificationsRead = () =>
  api.post('/notifications/mark_all_read/');

export const getNotificationPreferences = () =>
  api.get('/notifications/preferences/');

export const updateNotificationPreferences = (data) =>
  api.patch('/notifications/preferences/', data);

export const sendTestEmail = () =>
  api.post('/notifications/test_email/');

// Resolutions (Резолюции)
export const getResolutions = (params = {}) =>
  api.get('/resolutions/', { params });

export const createResolution = (data) =>
  api.post('/resolutions/', data);

export const executeResolution = (id) =>
  api.patch(`/resolutions/${id}/execute/`);

export const deleteResolution = (id) =>
  api.delete(`/resolutions/${id}/`);

// Хелпер для скачивания blob-ответа
export function downloadBlob(response, fallbackFilename) {
  const disposition = response.headers['content-disposition'];
  let filename = fallbackFilename;
  if (disposition) {
    const m = disposition.match(/filename\*=UTF-8''(.+)/i);
    if (m) filename = decodeURIComponent(m[1]);
  }
  // response.data уже Blob при responseType: 'blob' — используем напрямую
  const contentType = response.headers['content-type'] || '';
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ─── Tasks — Проекты и задачи ────────────────────────────────

// Команды
export const getTeams = (params = {}) =>
  api.get('/tasks/teams/', { params });

export const getTeam = (id) =>
  api.get(`/tasks/teams/${id}/`);

export const createTeam = (data) =>
  api.post('/tasks/teams/', data);

export const updateTeam = (id, data) =>
  api.patch(`/tasks/teams/${id}/`, data);

export const deleteTeam = (id) =>
  api.delete(`/tasks/teams/${id}/`);

export const addTeamMember = (teamId, data) =>
  api.post(`/tasks/teams/${teamId}/members/`, data);

export const removeTeamMember = (teamId, userId) =>
  api.delete(`/tasks/teams/${teamId}/members/${userId}/`);

// Проекты
export const getProjects = (params = {}) =>
  api.get('/tasks/projects/', { params });

export const getProject = (id) =>
  api.get(`/tasks/projects/${id}/`);

export const createProject = (data) =>
  api.post('/tasks/projects/', data);

export const updateProject = (id, data) =>
  api.patch(`/tasks/projects/${id}/`, data);

export const deleteProject = (id) =>
  api.delete(`/tasks/projects/${id}/`);

export const getProjectBoard = (id) =>
  api.get(`/tasks/projects/${id}/board/`);

export const getProjectMatrix = (id) =>
  api.get(`/tasks/projects/${id}/matrix/`);

export const getProjectWbs = (id) =>
  api.get(`/tasks/projects/${id}/wbs/`);

export const downloadProjectReport = (id) =>
  api.get(`/tasks/projects/${id}/report-docx/`, { responseType: 'blob' });

export const getProjectBaselines = (id) =>
  api.get(`/tasks/projects/${id}/baselines/`);

export const createProjectBaseline = (id, data) =>
  api.post(`/tasks/projects/${id}/baselines/`, data);

export const activateBaseline = (projectId, baselineId) =>
  api.post(`/tasks/projects/${projectId}/baselines/${baselineId}/activate/`);

export const deleteBaseline = (projectId, baselineId) =>
  api.delete(`/tasks/projects/${projectId}/baselines/${baselineId}/`);

export const reorderWbsTask = (projectId, data) =>
  api.post(`/tasks/projects/${projectId}/wbs-reorder/`, data);

export const downloadWbsPdf = (id) =>
  api.get(`/tasks/projects/${id}/wbs-pdf/`, { responseType: 'blob' });

export const downloadWbsXml = (id) =>
  api.get(`/tasks/projects/${id}/wbs-xml/`, { responseType: 'blob' });

export const importMsProjectXml = (projectId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/tasks/projects/${projectId}/wbs-import/`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const addTaskDependency = (projectId, data) =>
  api.post(`/tasks/projects/${projectId}/dependencies/`, data);

export const deleteTaskDependency = (projectId, depId) =>
  api.delete(`/tasks/projects/${projectId}/dependencies/${depId}/`);

export const downloadProjectMatrixDocx = (id) =>
  api.get(`/tasks/projects/${id}/matrix-docx/`, { responseType: 'blob' });

export const downloadProjectMatrixXlsx = (id) =>
  api.get(`/tasks/projects/${id}/matrix-xlsx/`, { responseType: 'blob' });

export const dispatchProjectToDepartments = (id, data) =>
  api.post(`/tasks/projects/${id}/dispatch/`, data);

// Задачи
export const getTasks = (params = {}) =>
  api.get('/tasks/tasks/', { params });

export const getTask = (id) =>
  api.get(`/tasks/tasks/${id}/`);

export const createTask = (data) =>
  api.post('/tasks/tasks/', data);

export const updateTask = (id, data) =>
  api.patch(`/tasks/tasks/${id}/`, data);

export const deleteTask = (id) =>
  api.delete(`/tasks/tasks/${id}/`);

export const changeTaskStatusV2 = (id, status) =>
  api.post(`/tasks/tasks/${id}/status/`, { status });

export const getTaskComments = (id) =>
  api.get(`/tasks/tasks/${id}/comments/`);

export const addTaskComment = (id, text) =>
  api.post(`/tasks/tasks/${id}/comments/`, { text });

export const getTaskHistory = (id) =>
  api.get(`/tasks/tasks/${id}/history/`);

// Личный кабинет + дашборд
export const getMyTasks = (params = {}) =>
  api.get('/tasks/my/', { params });

export const getTasksDashboard = () =>
  api.get('/tasks/dashboard/');

export const getResourceLoad = (params = {}) =>
  api.get('/tasks/resource-load/', { params });

// Вложения
export const getTaskAttachments = (taskId) =>
  api.get(`/tasks/tasks/${taskId}/attachments/`);

export const uploadTaskAttachment = (taskId, file, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/tasks/tasks/${taskId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
};

export const deleteTaskAttachment = (taskId, attachmentId) =>
  api.delete(`/tasks/tasks/${taskId}/attachments/${attachmentId}/`);

export const duplicateTask = (taskId, withSubtasks = false) =>
  api.post(`/tasks/tasks/${taskId}/duplicate/`, { with_subtasks: withSubtasks });

// Учёт времени
export const getTaskTimeLogs = (taskId) =>
  api.get(`/tasks/tasks/${taskId}/time/`);

export const addTaskTimeLog = (taskId, data) =>
  api.post(`/tasks/tasks/${taskId}/time/`, data);

export const deleteTaskTimeLog = (taskId, logId) =>
  api.delete(`/tasks/tasks/${taskId}/time/${logId}/`);

// Автоматизации
export const getAutomationRules = (params = {}) =>
  api.get('/tasks/automations/', { params });

export const createAutomationRule = (data) =>
  api.post('/tasks/automations/', data);

export const updateAutomationRule = (id, data) =>
  api.patch(`/tasks/automations/${id}/`, data);

export const deleteAutomationRule = (id) =>
  api.delete(`/tasks/automations/${id}/`);

export const toggleAutomationRule = (id) =>
  api.post(`/tasks/automations/${id}/toggle/`);


// Default export — объект со всеми функциями
export default {
  login,
  logout,
  getCurrentUser,
  getReports,
  getReport,
  createReport,
  updateReport,
  deleteReport,
  submitReport,
  getSubmissionStats,
  getMyReportTemplates,
  getLastReport,
  getTemplateByDepartment,
  getReportComments,
  getFieldComments,
  getFieldCommentSummary,
  createReportComment,
  deleteReportComment,
  resolveReportComment,
  unresolveReportComment,
  approveReport,
  rejectReport,
  bulkApproveReports,
  bulkRejectReports,
  getPeriods,
  getPeriod,
  getAnalytics,
  getReportTrends,
  // Documents
  getDocuments,
  getDocument,
  createDocument,
  updateDocument,
  deleteDocument,
  changeDocumentStatus,
  addDocumentItem,
  getMyDocuments,
  getMyPendingExecutions,
  getOverdueDocuments,
  getUpcomingDeadlines,
  getDocumentStatistics,
  getDocumentItems,
  getDocumentItem,
  changeItemStatus,
  addItemExecutor,
  completeExecution,
  uploadDocumentFile,
  // Справочники
  getDocumentTypes,
  getDocumentStatuses,
  getAuthorities,
  getPriorities,
  getDepartments,
  // Planning
  getPlans,
  getPlan,
  createPlan,
  updatePlan,
  deletePlan,
  activatePlan,
  completePlan,
  changePlanStatus,
  getMyPlans,
  getPlanStatistics,
  getPlanFact,
  getPlanTasks,
  getPlanTask,
  reportTaskProgress,
  changeTaskStatus,
  // Analytics
  getDashboardAnalytics,
  getDepartmentKPI,
  getTrends,
  getUserActivity,
  getExecutionDiscipline,
  comparePeriods,
  getDirectorOverview,
  globalSearch,
  getYearComparison,
  getRiskMap,
  getRatingHistory,
  getRatingTable,
  generateRatingSnapshots,
  getEmployeeKPI,
  // Accounts
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  getAuditLogs,
  // Profile
  getProfile,
  updateProfile,
  changePassword,
  // AI Assistant
  aiAsk,
  aiGetSummary,
  aiGenerateSummary,
  aiGetTrends,
  aiGenerateTrends,
  aiGetAnomalies,
  aiGenerateAnomalies,
  aiGetForecast,
  aiGenerateForecast,
  aiCompare,
  aiKeyMoments,
  aiSuggestedQuestions,
  aiQuickInsights,
  aiGetRecommendations,
  aiUpdateRecommendation,
  aiGenerateRecommendations,
  aiActiveRecommendations,
  aiGetConversations,
  aiSendFeedback,
  // Exports
  exportKpiExcel,
  exportKpiPdf,
  exportDisciplineExcel,
  exportDisciplinePdf,
  exportReportsExcel,
  exportDocumentsExcel,
  exportPlansExcel,
  exportPlanFactExcel,
  exportReportWord,
  exportAnalyticalWord,
  exportAnalyticalPdf,
  exportEmpKpiExcel,
  exportEmpKpiWord,
  downloadBlob,
  // Presentations
  getPresentationTemplates,
  getPresentationTemplate,
  createPresentationTemplate,
  updatePresentationTemplate,
  deletePresentationTemplate,
  createDefaultTemplates,
  getPresentations,
  getPresentation,
  createPresentation,
  updatePresentation,
  deletePresentation,
  generatePresentation,
  downloadPresentation,
  previewPresentation,
  regeneratePresentation,
  getPresentationStatistics,
  // Справочники CRUD
  getDirDepartments,
  createDirDepartment,
  updateDirDepartment,
  deleteDirDepartment,
  restoreDirDepartment,
  getDirAuthorities,
  createDirAuthority,
  updateDirAuthority,
  deleteDirAuthority,
  restoreDirAuthority,
  getDirDocumentTypes,
  createDirDocumentType,
  updateDirDocumentType,
  deleteDirDocumentType,
  restoreDirDocumentType,
  getDirDocumentStatuses,
  createDirDocumentStatus,
  updateDirDocumentStatus,
  deleteDirDocumentStatus,
  restoreDirDocumentStatus,
  getDirPriorities,
  createDirPriority,
  updateDirPriority,
  deleteDirPriority,
  restoreDirPriority,
  getDirTaskCategories,
  createDirTaskCategory,
  updateDirTaskCategory,
  deleteDirTaskCategory,
  restoreDirTaskCategory,
  // Системные настройки
  getSystemSettings,
  getSettingsByCategory,
  updateSystemSetting,
  bulkUpdateSettings,
  createSystemSetting,
  deleteSystemSetting,
  // Бэкапы
  getBackups,
  createBackup,
  downloadBackup,
  deleteBackup,
  // Weekly Control
  getWeeklySummary,
  getExecutionCheck,
  carryOverTasks,
  getCarryOverStats,
  getCenterPlan,
  getRelatedDepartments,
  // Report Constructor
  getConstructorTemplates,
  getConstructorTemplate,
  createConstructorTemplate,
  updateConstructorTemplate,
  deleteConstructorTemplate,
  getGeneratedReports,
  generateConstructorReport,
  downloadGeneratedReport,
  deleteGeneratedReport,
  // Расписания
  getScheduledReports,
  getScheduledReport,
  createScheduledReport,
  updateScheduledReport,
  deleteScheduledReport,
  toggleScheduledReport,
  runScheduledReportNow,
  // Консолидированные (сводные) отчёты
  getConsolidatedReports,
  getConsolidatedReport,
  createConsolidatedReport,
  updateConsolidatedReport,
  deleteConsolidatedReport,
  refreshConsolidatedData,
  updateConsolidatedNarratives,
  generateConsolidatedNarratives,
  generateConsolidatedDoc,
  downloadConsolidatedDoc,
  approveConsolidatedReport,
  // HR — Отдел кадров
  getEmployees,
  getEmployee,
  getEmployeesByDepartment,
  getAttendance,
  getAttendanceToday,
  getAttendanceSummary,
  bulkMarkAttendance,
  getAttendanceConfirmationStatus,
  confirmAttendance,
  unconfirmAttendance,
  getTransfers,
  createTransfer,
  uploadEmployeesPreview,
  uploadEmployeesConfirm,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  // KPI сотрудников
  getKPITemplates,
  getKPITemplate,
  getMyKPITemplate,
  createKPITemplate,
  updateKPITemplate,
  getKPIRecords,
  getKPIRecord,
  createKPIRecord,
  updateKPIRecord,
  getMyTeamKPI,
  submitKPIRecord,
  approveKPIRecord,
  rejectKPIRecord,
  autoCalculateKPI,
  bulkAutoCalculateKPI,
  downloadKPIWord,
  downloadKPISummaryExcel,
  getDailyKPIEntries,
  bulkDailyKPIEntry,
  getMonthlyKPITracker,
  // KPI-планы отделов
  getDeptKPIPlans,
  getDeptKPIPlan,
  createDeptKPIPlan,
  updateDeptKPIPlan,
  deleteDeptKPIPlan,
  submitDeptKPIPlan,
  approveDeptKPIPlan,
  rejectDeptKPIPlan,
  initDeptKPIPlanFromTemplate,
  autoAggregateDeptKPIPlan,
  downloadDeptKPIPlanWord,
  // Уведомления
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  getNotificationPreferences,
  updateNotificationPreferences,
  sendTestEmail,
  getResolutions,
  createResolution,
  executeResolution,
  deleteResolution,
  // Tasks (Проекты и задачи)
  getTeams,
  getTeam,
  createTeam,
  updateTeam,
  deleteTeam,
  addTeamMember,
  removeTeamMember,
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  getProjectBoard,
  getProjectMatrix,
  getProjectWbs,
  downloadProjectReport,
  getProjectBaselines,
  createProjectBaseline,
  activateBaseline,
  deleteBaseline,
  reorderWbsTask,
  downloadWbsPdf,
  downloadWbsXml,
  importMsProjectXml,
  addTaskDependency,
  deleteTaskDependency,
  downloadProjectMatrixDocx,
  downloadProjectMatrixXlsx,
  dispatchProjectToDepartments,
  getTasks,
  getTask,
  createTask,
  updateTask,
  deleteTask,
  changeTaskStatusV2,
  getTaskComments,
  addTaskComment,
  getTaskHistory,
  getMyTasks,
  getTasksDashboard,
  getResourceLoad,
  getTaskAttachments,
  uploadTaskAttachment,
  deleteTaskAttachment,
  duplicateTask,
  getTaskTimeLogs,
  addTaskTimeLog,
  deleteTaskTimeLog,
  getAutomationRules,
  createAutomationRule,
  updateAutomationRule,
  deleteAutomationRule,
  toggleAutomationRule,
};